import { type ReactNode, useState, useEffect } from 'react';
import { cornerstoneLogout, isCornerstoneEnabled } from '@/lib/cornerstone';
import { useNavigate } from 'react-router-dom';
import { AppShell } from '@/components/layout/app-shell';
import { api, ApiClient } from '@/lib/api-client';
import { adminApi } from '@/lib/admin-api-client';
import type { UserRole } from '@serviceready/shared';

const roleLabels: Record<string, string> = {
  pastor: 'Review Queue',
  minister_of_music: 'Music Department',
  media_team: 'Media Dashboard',
  musician: 'My Songs',
  submitter: 'My Submissions',
};

// Priority order: highest-privilege role wins (admin removed — platform superuser is separate)
const rolePriority: UserRole[] = ['pastor', 'minister_of_music', 'media_team', 'musician', 'submitter'];

interface ChurchInfo {
  id: string;
  name: string;
  slug: string;
}

function isSuperuserToken(): boolean {
  const token = ApiClient.getToken();
  if (!token) return false;
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload.superuser === true;
  } catch {
    return false;
  }
}

function getChurchIdFromToken(): string | null {
  const token = ApiClient.getToken();
  if (!token) return null;
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload.churchId || null;
  } catch {
    return null;
  }
}

interface AppLayoutProps {
  children: ReactNode;
}

export function AppLayout({ children }: AppLayoutProps) {
  const navigate = useNavigate();
  const [role, setRole] = useState<UserRole>('submitter');
  const [userRoles, setUserRoles] = useState<string[]>([]);
  const [title] = useState('ServiceReady');
  const [subtitle, setSubtitle] = useState('');
  const [loading, setLoading] = useState(true);
  const [isSuperuser, setIsSuperuser] = useState(false);
  const [churches, setChurches] = useState<ChurchInfo[]>([]);
  const [currentChurch, setCurrentChurch] = useState<string>('');
  const [switcherOpen, setSwitcherOpen] = useState(false);
  const [switching, setSwitching] = useState(false);

  const handleLogout = async () => {
    // A Cornerstone session lives in cookies on `.ministry.builders`, shared with
    // every other platform app. Clearing local state alone leaves those cookies
    // intact and the next visitor to this browser is silently signed back in —
    // so Cornerstone must be told first. cornerstoneLogout() also does the hard
    // navigation that destroys in-memory state.
    if (isCornerstoneEnabled()) {
      adminApi.clearToken();
      await cornerstoneLogout('/sign-in');
      return;
    }
    ApiClient.clearToken();
    adminApi.clearToken();
    navigate('/sign-in');
  };

  const handleSwitchChurch = async (churchId: string) => {
    if (churchId === getChurchIdFromToken()) {
      setSwitcherOpen(false);
      return;
    }
    setSwitching(true);
    try {
      const imp = await adminApi.impersonate(churchId);
      ApiClient.setToken(imp.token);
      setSwitcherOpen(false);
      // Reload the page to pick up new church data
      window.location.href = '/dashboard';
    } catch {
      setSwitching(false);
    }
  };

  useEffect(() => {
    const superuser = isSuperuserToken();
    setIsSuperuser(superuser);

    // If superuser, load the church list
    if (superuser && adminApi.getToken()) {
      adminApi.getMe().then(() => {
        // Fetch churches using the admin API client
        return adminApi.getChurches();
      }).then((churchList) => {
        setChurches(churchList.map(c => ({ id: c.id, name: c.name, slug: c.slug })));
        const currentId = getChurchIdFromToken();
        const current = churchList.find(c => c.id === currentId);
        if (current) setCurrentChurch(current.name);
      }).catch(() => {
        // Non-critical — switcher just won't show
      });
    }

    api.getMe()
      .then((data) => {
        const userData = data as { roles?: string[]; name?: string };
        const roles = userData.roles || [];
        const primaryRole = rolePriority.find((r) => roles.includes(r)) || 'submitter';
        setRole(primaryRole);
        setUserRoles(roles);
        setSubtitle(roleLabels[primaryRole]);
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
        ApiClient.clearToken();
        navigate('/sign-in');
      });
  }, [navigate]);

  if (loading) {
    return <div className="min-h-screen bg-warm-bg" />;
  }

  const canAccessSettings = isSuperuser || userRoles.includes('pastor') || userRoles.includes('admin');

  const headerActions = (
    <div className="flex items-center gap-2">
      {canAccessSettings && (
        <button
          onClick={() => navigate('/settings')}
          className="text-white/80 hover:text-white text-h2 p-1.5 rounded-lg hover:bg-white/10 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/60"
          title="Settings"
          aria-label="Settings"
        >
          {'\u2699\uFE0F'}
        </button>
      )}
      <button
        onClick={handleLogout}
        className="text-white/80 hover:text-white text-xs font-medium px-3 py-1.5 rounded-lg border border-white/20 hover:border-white/40 transition-colors"
      >
        Logout
      </button>
    </div>
  );

  const churchSwitcher = isSuperuser && churches.length > 0 ? (
    <div className="bg-primary text-white px-4 py-2 flex items-center justify-between text-xs">
      <span className="opacity-80">Viewing as admin:</span>
      <button
        onClick={() => setSwitcherOpen(!switcherOpen)}
        className="font-semibold flex items-center gap-1 hover:opacity-80 transition-opacity"
      >
        {currentChurch || 'Select Church'}
        <span className="text-micro">{switcherOpen ? '\u25B2' : '\u25BC'}</span>
      </button>
      {switcherOpen && (
        <div className="absolute top-full left-0 right-0 md:left-auto md:right-0 md:w-72 bg-white border border-[var(--color-border)] shadow-lg z-50">
          {churches.map((c) => (
            <button
              key={c.id}
              onClick={() => handleSwitchChurch(c.id)}
              disabled={switching}
              className={`w-full text-left px-4 py-3 text-sm hover:bg-gray-100 transition-colors border-b border-border last:border-0 ${
                c.id === getChurchIdFromToken() ? 'text-primary-dark font-semibold bg-[#F0FFF4]' : 'text-gray-700'
              }`}
            >
              <span className="block">{c.name}</span>
              <span className="text-xs text-text-secondary">{c.slug}</span>
            </button>
          ))}
          <button
            onClick={() => { navigate('/admin'); }}
            className="w-full text-left px-4 py-3 text-sm text-text-secondary hover:bg-gray-100 transition-colors font-medium"
          >
            Go to Admin Panel
          </button>
        </div>
      )}
    </div>
  ) : null;

  return (
    <AppShell role={role} title={title} subtitle={subtitle} headerAction={headerActions} topBanner={churchSwitcher}>
      {children}
    </AppShell>
  );
}
